import React from 'react';
import { alpha } from '@mui/material/styles';
import { SHAPE_TYPES, CANVAS_W, CANVAS_H } from '../constants';

const CHAT_COLOR = '#0a7aff';

/**
 * Renders one hotspot shape as SVG.
 *
 * Unselected: outline drawn with mix-blend-mode:difference so it's always
 *             visible as the negative of whatever background is behind it.
 * Selected:   filled + solid colored stroke using the hotspot's style settings.
 */
export function HotspotSVG({ hotspot, selected, previewMode = false, canvasW = CANVAS_W, canvasH = CANVAS_H }) {
  const shape    = SHAPE_TYPES[hotspot.shape] || SHAPE_TYPES.polygon;
  const color    = hotspot.glowColor || shape.color;
  const isActive = hotspot.isActive !== false;
  const isChat   = hotspot.hotspotType === 'chat';
  const isLink   = hotspot.hotspotType === 'link';

  const hasValidLink = isLink && (
    hotspot.hotspotAction?.targetId ||
    (hotspot.hotspotAction?.linkTarget === 'external' && hotspot.hotspotAction?.externalUrl)
  );

  const pxX = p => (p / 100) * canvasW;
  const pxY = p => (p / 100) * canvasH;

  // ── Style derivation ────────────────────────────────────────────────────────
  //
  // Builder (selected or unselected):
  //   Always uses the hotspot's configured hover style fields so what you see
  //   in the editor matches what appears in the live lobby on hover.
  //   Selected state adds a brighter border + glow to indicate selection.
  //
  // Preview mode: handled by LobbyEngine — its own HotspotShape component.

  const BRAND_BLUE    = '#1075BA';
  const fillColor     = isChat
    ? CHAT_COLOR
    : hotspot.hoverFillColor   || BRAND_BLUE;
  const fillOpacity   = isChat ? 0.2 : (hotspot.hoverFillOpacity  ?? 0.2);
  const borderColor   = isChat
    ? CHAT_COLOR
    : hotspot.hoverBorderColor || BRAND_BLUE;
  const borderOpacity = isChat ? 0.85 : (hotspot.hoverBorderOpacity ?? 0.85);
  const strokeWidth   = hotspot.strokeWidth ?? 1.5;

  // The base style mirrors how the hotspot looks on hover in the live lobby
  const baseShapeStyle = {
    fill:            alpha(fillColor,   fillOpacity),
    stroke:          alpha(borderColor, borderOpacity),
    strokeWidth,
    strokeDasharray: '0',
    mixBlendMode:    'normal',
  };

  // Selected: boost stroke to solid full-opacity + glow so selection is clear
  const selectedShapeStyle = {
    ...baseShapeStyle,
    stroke:      borderColor,
    strokeWidth: Math.max(strokeWidth, 2),
  };

  // Preview mode keeps separate behaviour (LobbyEngine renders its own shapes)
  const previewFill   = hasValidLink ? alpha(color, 0.18) : alpha(color, 0.08);
  const previewStroke = hasValidLink ? color : alpha(color, 0.3);
  const previewSW     = hasValidLink ? (hotspot.strokeWidth ?? 2) : 1;

  // Pick style
  const shapeStyle = previewMode
    ? { fill: previewFill, stroke: previewStroke, strokeWidth: previewSW, strokeDasharray: '0', mixBlendMode: 'normal' }
    : selected
      ? selectedShapeStyle
      : baseShapeStyle;

  const groupStyle = {
    opacity: isActive ? 1 : 0.4,
    filter: selected
      ? `drop-shadow(0 0 6px ${alpha(borderColor, 0.7)})`
      : previewMode && hasValidLink
        ? `drop-shadow(0 0 6px ${alpha(color, 0.5)})`
        : 'none',
  };

  // ── Type icon — uses exact MUI icon SVG paths, matching the TypeDropdown ────
  // Each icon path is defined on a 24×24 viewBox and scaled+translated to fit
  // the centroid of the hotspot shape at the right display size.
  function TypeIcon({ cx, cy, shapeW, shapeH }) {
    const dim  = Math.min(shapeW ?? 60, shapeH ?? 60);
    const size = Math.max(dim * 0.38, 24);  // display size in canvas px, min 24
    const ic   = borderColor;

    // Scale from 24×24 viewBox into a `size×size` box centred at (cx, cy)
    const scale = size / 24;
    const tx    = cx - size / 2;
    const ty    = cy - size / 2;
    const tf    = `translate(${tx},${ty}) scale(${scale})`;

    const MUI_PATHS = {
      link:    'M17 7h-4v2h4c1.65 0 3 1.35 3 3s-1.35 3-3 3h-4v2h4c2.76 0 5-2.24 5-5s-2.24-5-5-5zm-6 8H7c-1.65 0-3-1.35-3-3s1.35-3 3-3h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-2zm-3-4h8v2H8z',
      video:   'M10 16.5l6-4.5-6-4.5v9zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z',
      gallery: 'M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4 2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z',
      iframe:  'M9.4 16.6 4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0 4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z',
      chat:    'M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z',
    };

    const pathD = MUI_PATHS[hotspot.hotspotType] || MUI_PATHS.link;

    return (
      <g pointerEvents="none" transform={tf} opacity={0.9}>
        <path d={pathD} fill={ic} />
      </g>
    );
  }

  // ── Chat hint ────────────────────────────────────────────────────────────
  function ChatHint({ cx, by, bh }) {
    if (!selected) return null;
    return (
      <text x={cx} y={by + bh + 18} textAnchor="middle" dominantBaseline="middle"
        fontSize={11} fill={CHAT_COLOR} fontWeight={600} fontFamily="inherit"
        pointerEvents="none" opacity={0.85}>
        ↕ Double-click to open chat
      </text>
    );
  }

  // ── Preview label ────────────────────────────────────────────────────────
  function PreviewLabel({ cx, cy }) {
    if (!previewMode || !hasValidLink) return null;
    const isExternal = hotspot.hotspotAction?.linkTarget === 'external';
    const rawLabel   = isExternal
      ? `↗ ${hotspot.hotspotAction.externalUrl}`
      : `→ ${hotspot.label || 'Go'}`;
    const display = rawLabel.length > 22 ? rawLabel.slice(0, 22) + '…' : rawLabel;
    return (
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
        fontSize={11} fill={color} fontWeight={700} fontFamily="inherit"
        pointerEvents="none" opacity={0.9}>
        {display}
      </text>
    );
  }

  // ── Shape renderers ──────────────────────────────────────────────────────

  // Bezier polygon
  if (hotspot.shape === 'polygon' && hotspot.meta?.isBezier && hotspot.meta?.pathD) {
    const pts = hotspot.points || [];
    const xs  = pts.map(p => pxX(p.x)), ys = pts.map(p => pxY(p.y));
    const cx  = pts.length ? (Math.min(...xs) + Math.max(...xs)) / 2 : 0;
    const cy  = pts.length ? (Math.min(...ys) + Math.max(...ys)) / 2 : 0;
    return (
      <g style={groupStyle}>
        <path d={hotspot.meta.pathD} {...shapeStyle} />
        {previewMode
          ? <PreviewLabel cx={cx} cy={cy} />
          : <TypeIcon cx={cx} cy={cy} shapeW={xs.length ? Math.max(...xs)-Math.min(...xs) : 60} shapeH={ys.length ? Math.max(...ys)-Math.min(...ys) : 60} />}
      </g>
    );
  }

  // Rect
  if (hotspot.shape === 'rect' && hotspot.points?.[0] && hotspot.meta) {
    const x  = pxX(hotspot.points[0].x), y  = pxY(hotspot.points[0].y);
    const w  = pxX(hotspot.meta.width  || 10), h = pxY(hotspot.meta.height || 10);
    const cx = x + w / 2, cy = y + h / 2;
    return (
      <g style={groupStyle}>
        <rect x={x} y={y} width={w} height={h} rx={isChat ? 6 : 4}
          {...shapeStyle}
          fill={shapeStyle.fill}
          stroke={shapeStyle.stroke}
          mixBlendMode="normal"
        />
        {previewMode
          ? <PreviewLabel cx={cx} cy={cy} />
          : <TypeIcon cx={cx} cy={cy} shapeW={w} shapeH={h} />}
        {isChat && <ChatHint cx={cx} by={y} bh={h} />}
      </g>
    );
  }

  // Circle
  if (hotspot.shape === 'circle' && hotspot.points?.[0]) {
    const cx = pxX(hotspot.points[0].x), cy = pxY(hotspot.points[0].y);
    const r  = pxX(hotspot.meta?.radius || 5);
    return (
      <g style={groupStyle}>
        <circle cx={cx} cy={cy} r={r}
          {...shapeStyle}
          fill={shapeStyle.fill}
          stroke={shapeStyle.stroke}
          mixBlendMode="normal"
        />
        {previewMode
          ? <PreviewLabel cx={cx} cy={cy} />
          : <TypeIcon cx={cx} cy={cy} shapeW={r*2} shapeH={r*2} />}
        {isChat && <ChatHint cx={cx} by={cy - r} bh={r * 2} />}
      </g>
    );
  }

  // Straight polygon
  if (hotspot.points?.length > 1) {
    const ptsStr = hotspot.points.map(p => `${pxX(p.x)},${pxY(p.y)}`).join(' ');
    const xs = hotspot.points.map(p => pxX(p.x)), ys = hotspot.points.map(p => pxY(p.y));
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
    const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
    return (
      <g style={groupStyle}>
        <polygon points={ptsStr} {...shapeStyle} />
        {!previewMode && <TypeIcon cx={cx} cy={cy} shapeW={Math.max(...xs)-Math.min(...xs)} shapeH={Math.max(...ys)-Math.min(...ys)} />}
      </g>
    );
  }

  return null;
}